"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, UserCheck, Calendar, Mail, Phone, ArrowRight, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const features = [{
  title: "100% Direct Access",
  description: "You work directly with me, Yassine. No middle management, junior staff, or account managers."
}, {
  title: "Customized Strategy",
  description: "Tailored SEO roadmap aligned specifically with your commercial goals and target markets."
}, {
  title: "No Long Contracts",
  description: "Transparent deliverables, straightforward communication, and flexible engagement models."
}];
function CTAForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl bg-muted p-8 text-center border border-border">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Consultation Requested</h3>
        <p className="mt-2 text-sm text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Yassine Essaadi" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="yassine@example.com" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Please enter your website URL"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourcompany.com" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold transition-colors duration-200 mt-2">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Scheduling Call...
          </> : <>
            <SafeHydrate><Calendar className="mr-2 size-4" /></SafeHydrate>
            Book My Free SEO Strategy Session
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground pt-1">
        No obligation. I review your website metrics personally before our call.
      </p>
    </Form>;
}
export default function ConsultationCTA() {
  return <section id="consultationcta" data-nav="light" className="relative py-20 md:py-28 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium">
              <UserCheck className="size-4 text-primary" />
              <span>Direct Expert Partnership</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default leading-tight">
              Ready to Accelerate Your Organic Search Growth?
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              When you choose YE Consultancy, you work directly with me, Yassine Essaadi. I bring over a decade of hands-on technical and strategic SEO experience to help your website capture high-intent organic leads.
            </p>

            <div className="pt-2 space-y-4">
              {features.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3">
                  <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6 border-t border-border flex flex-col sm:flex-row gap-4 sm:items-center">
              <Link to="/ContactPage">
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-border">
                  Direct Contact Page
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <Mail className="size-3.5" />
                  <span>contact@ye-consultancy.com</span>
                </a>
                <span>•</span>
                <a href="tel:+447490984430" className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <Phone className="size-3.5" />
                  <span>+44 7490984430</span>
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="lg:col-span-5">
            <Card className="border-border shadow-xl bg-card">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-6 space-y-1 text-center">
                  <div className="inline-flex items-center justify-center size-10 rounded-full bg-accent/20 text-accent-foreground mb-2">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h3 className="text-xl font-bold text-card-foreground font-default">
                    Book a Free SEO Consultation
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Get an honest audit and direct roadmap from Yassine.
                  </p>
                </div>

                <FormProvider formId="consultation-cta-form" sectionName="consultation-cta" defaultValues={{
                name: "",
                email: "",
                website: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book My Free SEO Strategy Session" successMessage="Thank you! Yassine will review your website and contact you within 24 hours.">
                  <CTAForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}